import { Box, Card, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { getFhirIdFromEntity, getReferenceDisplayByResource } from 'src/utils/fhir';
import useObjectState from 'src/hooks/useObjectState';
import { useStructureDefinitionsFiltered } from 'src/hooks/useStructureDefinitionsFiltered';
import CustomField from './CustomField';
import CustomModal from 'src/components/CustomModal';
import produce from 'immer';
import moment from 'moment';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { capitalize } from 'src/utils/string';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedStructureDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/StructureDefinition';
import { usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ReferenceResources, RequesterResourceTypes } from 'src/careflow/helpers/types';
import SearchMember from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import { getExternalResourceType } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { EditableCard } from 'src/careflow/common/EditableCard';

type Props = {
  patient: WrappedPatient;
  open: boolean;
  structureDef: WrappedStructureDefinition[] | null;
  configurations: any;
  isLoading?: boolean;
  i18n: any;
  onClose: VoidFunction;
};

const normalizeForSave = (values: any[]) =>
  values.map((v) => {
    const key = Object.keys(v).find((k) => k !== 'url')!;
    const raw = v[key];

    if (key === 'valueDecimal') {
      return {
        ...v,
        [key]: raw === '' ? null : Number(raw),
      };
    }

    return v;
  });

const EditMode = ({
  patient,
  open,
  structureDef,
  configurations,
  isLoading,
  i18n,
  onClose,
}: Props) => {
  const [, { update }] = usePatient(patient.id!, { map: PatientWrapper });

  const [
    { value, isSearchMemberOpen, selectedSearchMember, searchMemberOption, errors },
    updateState,
  ] = useObjectState<{
    value: any;
    isSearchMemberOpen: boolean;
    selectedSearchMember: RequesterResourceTypes | ReferenceResources | null;
    searchMemberOption: any;
    errors: any;
  }>({
    value: null,
    isSearchMemberOpen: false,
    selectedSearchMember: null,
    searchMemberOption: null,
    errors: null,
  });

  const customFields = useCallback(() => {
    const values =
      patient?.extension?.reduce((acc, e) => {
        const url = e?.url;
        const { key, value } = getExtensionValue(e) ?? {};

        if (!key) return acc;

        acc.push({ url, [key]: value });
        return acc;
      }, [] as any[]) ?? [];

    updateState({ value: values });
  }, [patient?.extension]);

  useEffect(() => {
    if (!open || !patient) return;
    customFields();
  }, [open, customFields]);

  const handlerUpdate = async () => {
    await update(
      produce(patient!, (draft) => {
        draft.extension ??= [];
        draft.extension = normalizeForSave(value);
      })
    );
    handleClose();
  };

  const handleClose = () => {
    updateState({
      value: null,
      isSearchMemberOpen: false,
      selectedSearchMember: null,
      searchMemberOption: null,
      errors: null,
    });
    onClose();
  };

  if (!open) return null;

  return (
    <CustomModal
      open={open}
      title={i18n('customFields.details.title', 'crs')}
      breadcrumbs={[]}
      onSave={handlerUpdate}
      onCancel={handleClose as Function}
      isLoading={isLoading}
      containerSx={[{ overflow: 'scroll' }, isSearchMemberOpen ? { width: '94vw' } : {}]}
    >
      <Card sx={{ mt: 2 }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={isSearchMemberOpen ? 6 : 12}>
            <Stack direction="column" spacing={2}>
              {structureDef?.map((e, index) => (
                <CustomField
                  key={`custom-field-${index}`}
                  element={e}
                  value={value}
                  patient={patient}
                  errors={errors}
                  configurations={configurations}
                  selectedSearchMember={selectedSearchMember}
                  isLoading={isLoading}
                  updateState={updateState}
                />
              ))}
            </Stack>
          </Grid>
          {isSearchMemberOpen && (
            <Grid item xs={6} sx={{ marginTop: 0 }}>
              <SearchMember
                patient={patient as WrappedPatient}
                onSelectResource={(resource, includedResources) => {
                  const display = getReferenceDisplayByResource(resource, includedResources ?? []);
                  const { url, valueKey } = searchMemberOption ?? {};
                  updateState((prev) => ({
                    ...prev,
                    selectedSearchMember: {
                      reference: `${resource.resourceType}/${resource.id}`,
                      display,
                    },
                    value: [
                      ...(prev.value ?? []).filter((v) => v.url !== url),
                      {
                        url,
                        [valueKey]: {
                          reference: `${resource.resourceType}/${resource.id}`,
                          display,
                        },
                      },
                    ],
                    isSearchMemberOpen: false,
                  }));
                }}
                externalResourceType={
                  selectedSearchMember ? getExternalResourceType(selectedSearchMember) : undefined
                }
                onClear={() => {
                  updateState({ isSearchMemberOpen: false });
                }}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    </CustomModal>
  );
};

type DisplayModeProps = {
  patient: WrappedPatient;
  structureDef: WrappedStructureDefinition[] | null;
};

const getExtensionValue = (ext: any) => {
  if (!ext) return undefined;

  const valueKey = Object.keys(ext).find((key) => key.startsWith('value'));

  return valueKey
    ? {
        key: valueKey,
        value: ext[valueKey],
      }
    : undefined;
};

const DisplayMode = ({ patient, structureDef }: DisplayModeProps) => {
  const structureDefinitionMap = useMemo(
    () => new Map(structureDef?.map((o) => [o.url, o])),
    [structureDef]
  );

  const customFields = useMemo(() => {
    if (!patient?.extension?.length) return [];

    return patient.extension.map((extension) => {
      const url = getFhirIdFromEntity(extension?.url);
      const structureDefintion: any = structureDefinitionMap?.get(extension?.url);
      const { key = null, value = null } = getExtensionValue(extension) ?? {};
      let valueToDisplay: any;

      switch (key) {
        case 'valueBoolean':
          valueToDisplay =
            value === true ? (
              <CheckBox color="disabled" />
            ) : (
              <CheckBoxOutlineBlank color="disabled" />
            );
          break;
        case 'valueDateTime':
          valueToDisplay = value ? moment(value).format('MMM D Y') : '';
          break;
        case 'valueCodeableConcept':
          valueToDisplay = value ? value?.coding?.[0]?.display : '';
          break;
        case 'valueReference':
          valueToDisplay = value ? value?.display : '';
          break;
        default:
          valueToDisplay = value ?? '';
      }

      return {
        label: structureDefintion?.title ?? url,
        value: valueToDisplay,
      };
    });
  }, [patient?.extension, structureDefinitionMap]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container sx={{ p: 1 }}>
        {customFields?.map((e, index) => (
          <Grid item xs={3} key={index}>
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1, fontWeight: 'bold' }}>
              {e?.label ? capitalize(e?.label) : ''}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                textTransform: 'capitalize',
              }}
            >
              {e?.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

type PropDetails = {
  patient: WrappedPatient;
};

export default function CustomFieldsDetails({ patient }: PropDetails) {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();

  const authUser = useAuth().getCurrentUser();
  const [openCustomFieldsDetails, setCustomFieldsDetails] = useState(false);

  const { customFields, searchMember } = componentsData ?? {};
  const { allUserOrganizations } = customFields ?? {};
  const { searchMemberOptions } = searchMember ?? {};

  const { structureDefinitionOrg, isLoading } = useStructureDefinitionsFiltered({
    authUser,
    allUserOrganizations,
    status: 'draft,active,unknown',
  });

  return (
    <EditableCard
      title={i18n('customFields.details.title', 'crs')}
      onEditButtonPress={() => setCustomFieldsDetails(true)}
      button={
        checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CUSTOM_FIELDS.EDIT] }) && (
          <IconButton onClick={() => setCustomFieldsDetails(true)} sx={{ p: 0.5, ml: 1 }}>
            <Iconify icon={openCustomFieldsDetails ? 'eva:save-outline' : 'eva:edit-outline'} />
          </IconButton>
        )
      }
    >
      <DisplayMode patient={patient} structureDef={structureDefinitionOrg} />
      <EditMode
        patient={patient}
        open={openCustomFieldsDetails}
        structureDef={structureDefinitionOrg}
        configurations={searchMemberOptions?.customFieldReference}
        isLoading={isLoading}
        i18n={i18n}
        onClose={() => setCustomFieldsDetails(false)}
      />
    </EditableCard>
  );
}
